import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { stockLevelApi, stockLotApi, stockMovementApi } from '../services/inventoryApi';
import type {
  ListStockLevelsParams,
  ListStockLotsParams,
  ListStockMovementsParams,
  StockLevelLookupParams,
  StockLotCreate,
} from '../types';

export const stockLevelKeys = {
  all: ['stock-levels'] as const,
  list: (params?: ListStockLevelsParams) => [...stockLevelKeys.all, 'list', params] as const,
  lookup: (params: StockLevelLookupParams) => [...stockLevelKeys.all, 'lookup', params] as const,
  summary: (productId: string) => [...stockLevelKeys.all, 'summary', productId] as const,
  rollup: (productId: string, warehouseId?: string) =>
    [...stockLevelKeys.all, 'rollup', productId, warehouseId] as const,
};

export const stockMovementKeys = {
  all: ['stock-movements'] as const,
  list: (params?: ListStockMovementsParams) => [...stockMovementKeys.all, 'list', params] as const,
  detail: (id: string) => [...stockMovementKeys.all, 'detail', id] as const,
};

export const stockLotKeys = {
  all: ['stock-lots'] as const,
  list: (params: ListStockLotsParams) => [...stockLotKeys.all, 'list', params] as const,
  detail: (id: string) => [...stockLotKeys.all, 'detail', id] as const,
};

export function useStockLevels(params?: ListStockLevelsParams) {
  return useQuery({
    queryKey: stockLevelKeys.list(params),
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => stockLevelApi.list(params),
  });
}

/** Exact-match lookup for a single stock level row. Returns `null` when no
 *  matching `(warehouse, product, variant, lot, location)` row exists —
 *  callers must treat that as "0 on hand," not as an error. The quantity it
 *  returns is the product's BASE unit, whatever unit the caller enters in. */
export function useStockLevelLookup(params: StockLevelLookupParams | null) {
  return useQuery({
    queryKey: stockLevelKeys.lookup(params as StockLevelLookupParams),
    queryFn: () => stockLevelApi.lookup(params as StockLevelLookupParams),
    enabled: !!params,
  });
}

export function useStockLevelSummary(productId: string) {
  return useQuery({
    queryKey: stockLevelKeys.summary(productId),
    queryFn: () => stockLevelApi.summary(productId),
    enabled: !!productId,
  });
}

export function useStockMovements(params?: ListStockMovementsParams) {
  return useQuery({
    queryKey: stockMovementKeys.list(params),
    queryFn: () => stockMovementApi.list(params),
  });
}

export function useStockMovement(id: string | null) {
  return useQuery({
    queryKey: stockMovementKeys.detail(id ?? ''),
    queryFn: () => stockMovementApi.get(id as string),
    enabled: !!id,
  });
}

export function useStockLots(params: ListStockLotsParams | null) {
  return useQuery({
    queryKey: stockLotKeys.list(params as ListStockLotsParams),
    queryFn: () => stockLotApi.list(params as ListStockLotsParams),
    enabled: !!params?.product_id,
  });
}

export function useCreateStockLot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: StockLotCreate) => stockLotApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stockLotKeys.all });
    },
  });
}
